import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { TransferActivityResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
interface TransferActivityCardPDFProps {
  transferActivity: TransferActivityResponseDto;
  customizationSettings?: PdfCustomizationSettings;
}

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  metaContainer: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 15,
    backgroundColor: "#f3f4f6",
    padding: 8,
    borderRadius: 6,
  },
  badge: {
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
  },
  durationBadge: {
    backgroundColor: "#dbeafe",
    color: "#1e40af",
  },
  section: {
    marginBottom: 15,
    padding: 12,
    borderRadius: 8,
  },
  descriptionSection: {
    backgroundColor: "#f0f9ff",
  },
  connectionSection: {
    backgroundColor: "#ecfdf5",
  },
  taskSection: {
    backgroundColor: "#fef3c7",
  },
  teacherSection: {
    backgroundColor: "#fff7ed",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#1e3a8a",
  },
  sectionContent: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.5,
  },
  gridContainer: {
    flexDirection: "row",
    gap: 12,
    marginVertical: 12,
  },
  gridColumn: {
    flex: 1,
  },
});

const TransferActivityDocument = ({
  transferActivity,
  customizationSettings,
}: TransferActivityCardPDFProps) => {
  const { t } = useTranslation();

  return (
    <PdfLayout
      courseName={transferActivity.courseName ?? ""}
      gradeLevel={transferActivity.gradeLevel ?? ""}
      topic={transferActivity.title || transferActivity.prompt || ""}
      imageUrl={transferActivity.imageUrl ?? ""}
      headerTextColor="#2563eb"
      customizationSettings={customizationSettings}
      createdDate={transferActivity.createdAt}
      author={transferActivity.authorName ?? ""}
    >
      <View wrap={false} style={[styles.section, styles.descriptionSection]}>
        <Text
          style={[
            styles.sectionTitle,
            {
              fontSize: scaleFontSize(
                styles.sectionTitle.fontSize,
                customizationSettings?.fontSize,
              ),
            },
          ]}
        >
          {t("transferActivities.description")}
        </Text>
        <MarkdownRenderer
          customStyle={{
            text: {
              text: {
                ...styles.sectionContent,
                fontSize: scaleFontSize(
                  styles.sectionContent.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
              container: { marginBottom: 8 },
            },
          }}
          content={transferActivity.description ?? ""}
        />
      </View>
      <View wrap={false} style={styles.gridContainer}>
        <View style={styles.gridColumn}>
          <View style={[styles.section, styles.connectionSection]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("transferActivities.realLifeConnection")}
            </Text>
            <MarkdownRenderer
              customStyle={{
                text: {
                  text: {
                    ...styles.sectionContent,
                    fontSize: scaleFontSize(
                      styles.sectionContent.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: { marginBottom: 8 },
                },
              }}
              content={transferActivity.realLifeConnection ?? ""}
            />
          </View>
        </View>

        <View style={styles.gridColumn}>
          <View style={[styles.section, styles.taskSection]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("transferActivities.studentTask")}
            </Text>
            <MarkdownRenderer
              customStyle={{
                listItem: {
                  text: {
                    ...styles.sectionContent,
                    fontSize: scaleFontSize(
                      styles.sectionContent.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: { marginBottom: 8 },
                },
              }}
              content={transferActivity.studentTask ?? ""}
            />
          </View>
        </View>
      </View>
      <View
        wrap={false}
        style={[styles.section, styles.teacherSection, { marginBottom: 0 }]}
      >
        <Text
          style={[
            styles.sectionTitle,
            {
              fontSize: scaleFontSize(
                styles.sectionTitle.fontSize,
                customizationSettings?.fontSize,
              ),
            },
          ]}
        >
          {t("transferActivities.teacherRole")}
        </Text>
        <MarkdownRenderer
          customStyle={{
            text: {
              text: {
                ...styles.sectionContent,
                fontSize: scaleFontSize(
                  styles.sectionContent.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
              container: { marginBottom: 8 },
            },
          }}
          content={transferActivity.teacherRole ?? ""}
        />
      </View>
    </PdfLayout>
  );
};

const TransferActivityCardPDF = (props: TransferActivityCardPDFProps) => {
  return <TransferActivityDocument {...props} />;
};

export default TransferActivityCardPDF;
